<template>
	<div class="col-12">
		<div class="card">
			<h4>
				Customer Details
				<a @click.exact="navigation.toCompanyDetailsScreen(customerCompanyId)" @click.shift="navigation.toCompanyDetailsScreen(customerCompanyId,true)" v-if="customerCompanyId != null && isInternalUser">
					<i class="pi pi-external-link p-1" style="font-size: 1rem;" />
				</a>
			</h4>
			<div class="fluid grid">

				<!--Customer-->
				<div class="field col-12 xxl:col-4 align-self-end">
					<label>Customer name</label>
					<div class="flex">
						<Multiselect :options="companyList"
									 :searchable="true"
									 :limit="40"
									 :filter="true"
									 class="w-100pc"
									 v-model="selectedCustomerCompanyId"
									 valueProp="id"
									 label="nameAndId"
									 track-by="nameAndId"
									 placeholder="Select Customer" />
					</div>
				</div>

				<!--Billing Customer-->
				<div class="field col-12 xxl:col-4 align-self-end">
					<label>Billing Customer (different company/address/contacts)</label>
					<div class="flex">
						<Multiselect :options="companyList"
									 :searchable="true"
									 :limit="40"
									 :filter="true"
									 class="w-100pc"
									 v-model="selectedBillingCustomerCompanyId"
									 valueProp="id"
									 label="nameAndId"
									 track-by="nameAndId"
									 placeholder="Select Billing Company (if different company)" />
					</div>
				</div>

				<!--Billing Entity-->
				<div class="field col-12 xxl:col-4 align-self-end">
					<label>Billing Name (same company/address/contacts)</label>
					<div class="flex">
						<Multiselect :options="billingEntityList"
									 :searchable="true"
									 :limit="40"
									 :filter="true"
									 class="w-100pc"
									 v-model="selectedBillingEntityId"
									 valueProp="billingEntityId"
									 label="billingEntityName"
									 track-by="billingEntityName"
									 noOptionsText="(No alternate billing names have been entered for these companies)"
									 placeholder="Select Alternative Billing Name" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { BillingEntityData, CompanyListData } from '../api-client/api-client'
	import { CompassApi } from '@/api-client/CompassApiProxy'
	import { Navigation } from '@/utils/Navigation';

	@Options({
		name: 'PropertyCustomerDetails',
		components: {
		},
		props: {
			customerCompanyId: 0,
			billingEntityCompanyId: 0,
			receivedBillingEntity: BillingEntityData
		},
		watch: {
			selectedCustomerCompanyId(value: number) {
				this.$emit('updateCustomerCompanyId', value);
				this.getBillingEntityList();
			},
			selectedBillingCustomerCompanyId(value: number) {
				this.$emit('updateBillingEntityCompanyId', value);
				this.getBillingEntityList();
			},
			selectedBillingEntityId(value?: number) {
				// Dont update the billing entity id with a NULL if it's undefined (We do not send NULL values to the client anymore)
				if (value == null)
					value = undefined;

				this.$emit('updateBillingEntityId', value);
				this.$emit('updateBillingEntity', this.selectedBillingEntity)
			}
		}
	})
	export default class PropertyCustomerDetails extends Vue {
		/** Component prop for initially selected customer  */
		customerCompanyId = 0;

		/** Component prop for initially selected billing company */
		billingEntityCompanyId = 0;

		/** Component prop  for initially selected billing entity name */
		receivedBillingEntity!: BillingEntityData;

		/** Dropdown selection, must be initialised as a number for multiselect to work */
		selectedCustomerCompanyId = 0;

		/** Dropdown selection, must be initialised as a number for multiselect to work */
		selectedBillingCustomerCompanyId = 0;

		/** Dropdown selection, must be initialised as a number for multiselect to work */
		selectedBillingEntityId = 0;

		/** Options to show in the dropdown */
		billingEntityList: BillingEntityData[] = []

		/** We retrieve the billing entity list on a number of events, we can remember the parameters, so we don't call again if the same */
		lastBillingEntityListCriteria = '';

		isInternalUser = false;

		navigation = Navigation;

		/** Event on component instantiation */
		created(): void {
			this.isInternalUser = this.$store.getters.isInternalUser;

			this.selectedCustomerCompanyId = this.customerCompanyId;
			this.selectedBillingCustomerCompanyId = this.billingEntityCompanyId;

			if (this.receivedBillingEntity) {
				this.billingEntityList.push(this.receivedBillingEntity);
				this.selectedBillingEntityId = this.receivedBillingEntity.billingEntityId as number;
			} else {
				(this.selectedBillingEntityId as unknown) = null;
			}

			if (this.customerCompanyId == null) {
				return;
			}

			this.getBillingEntityList();
		}

		/** Retrieve billing entities related to customer or billing company */
		getBillingEntityList(): void {
			const companyIds: number[] = [];
			if (this.selectedCustomerCompanyId > 0) companyIds.push(this.selectedCustomerCompanyId);
			if (this.selectedBillingCustomerCompanyId > 0) companyIds.push(this.selectedBillingCustomerCompanyId);

			if (companyIds.length == 0) {
				this.billingEntityList = [];
				return;
			}

			const billingEntityListCriteria = JSON.stringify(companyIds);

			// Don't need to call API if its the same criteria
			if (billingEntityListCriteria == this.lastBillingEntityListCriteria) {
				return;
			}

			// Remember the criteria so we don't need to repeat the call
			this.lastBillingEntityListCriteria = billingEntityListCriteria;

			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.getBillingEntities(companyIds)
				.then(result => {
					let receivedEntityInList = false;
					this.billingEntityList = result;

					// Add the received entity to the billing entity list if it's not there
					if (this.receivedBillingEntity) {
						this.billingEntityList.forEach(be => be.billingEntityId == this.receivedBillingEntity.billingEntityId ? receivedEntityInList = true : receivedEntityInList = false);
						if (!receivedEntityInList)
							this.billingEntityList.push(this.receivedBillingEntity);
					}
				});
		}

		/** Property reference to the central company list store */
		get companyList(): CompanyListData[] {
			return this.$store.state.companyList.data;
		}

		/** Get the billing entity matching selected ID */
		get selectedBillingEntity(): BillingEntityData {
			const billingEntity: BillingEntityData = (this.billingEntityList.find(b => b.billingEntityId == this.selectedBillingEntityId) as BillingEntityData);
			return billingEntity
		}
	}
</script>

<style scoped lang="scss">
</style>